import { Suspense, useMemo, useRef, useState } from 'react'
import type { FC } from 'react'
import { Box } from '@mui/material'
import { Navigate, Route, Routes } from 'react-router'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorScreen, Loader } from '~/shared/ui'
import { useFeatureAccessQuery } from '~/shared/hooks/useFeatureAccess'
import { useFeatureFlags } from '~/shared/hooks/useFeatureFlag'
import { useFeatureSwitches } from '~/shared/hooks/useFeatureSwitch'
import {
  BENEFIT_PROGRAM_UID,
  PRIMARY_CARE_PROGRAM_UID,
  useListProgramEnrollments,
} from '~/entities/program'
import { type FeatureAccess, type PatientUser, type Person } from '~/shared/types'
import PatientDetailNavStrip from './PatientDetailNavStrip'
import { DEFAULT_ITEM_PATH, NAV_TREE } from './navConfig'
import type { NavItem, NavMode } from './navConfig'
import { useRouteBasePath } from '~/shared/hooks/useRouteBasePath'

interface PatientDetailNavProps {
  patient: PatientUser
  mode?: NavMode
  person?: Person
}

const itemIsEnabled = (
  item: NavItem,
  featureSwitches: Record<string, { active?: boolean }> | undefined,
  featureFlags: Record<string, { is_active: boolean | null }> | undefined,
  featureAccess: FeatureAccess | undefined,
) =>
  (item.featureSwitch ? featureSwitches?.[item.featureSwitch]?.active : true) &&
  (item.featureFlag ? featureFlags?.[item.featureFlag]?.is_active : true) &&
  (item.feature ? featureAccess?.tenant?.[item.feature] : true)

const PatientDetailNav: FC<PatientDetailNavProps> = ({ patient, mode = 'patient', person }) => {
  const basePath = useRouteBasePath()
  const featureSwitchesQuery = useFeatureSwitches()
  const featureFlagsQuery = useFeatureFlags()
  const featureAccessQuery = useFeatureAccessQuery()
  const featureSwitches = featureSwitchesQuery.data?.featureSwitchesByName
  const featureFlags = featureFlagsQuery.data?.featureFlagsByName
  const featureAccess = featureAccessQuery.data

  // Category visibility gated by program enrollment in both modes:
  // - Health Plan: requires a Benefit program enrollment.
  // - Clinical:    requires a Primary Care program enrollment (patient mode only;
  //                person mode shows Clinical for its items that need no patient
  //                account — ZAP and Care Gaps, both keyed off the Person).
  // Person mode additionally hides items/categories marked `hideInPersonMode` in NAV_TREE.
  const personId = (patient.person?.id ?? person?.id) as number | undefined
  const programEnrollmentsQuery = useListProgramEnrollments(personId as number)
  const programEnrollments = programEnrollmentsQuery.data
  const hasBenefitEnrollment = useMemo(
    () => !!programEnrollments?.some(e => e.program === BENEFIT_PROGRAM_UID),
    [programEnrollments],
  )
  const hasPrimaryCareEnrollment = useMemo(
    () => !!programEnrollments?.some(e => e.program === PRIMARY_CARE_PROGRAM_UID),
    [programEnrollments],
  )

  const visibleCategories = useMemo(() => {
    return NAV_TREE.flatMap(category => {
      if (mode === 'person' && category.hideInPersonMode) return []
      if (category.id === 'healthPlan' && !hasBenefitEnrollment) return []
      if (mode === 'patient' && category.id === 'clinical' && !hasPrimaryCareEnrollment) return []
      const items = category.items.filter(item => {
        if (mode === 'person' && item.hideInPersonMode) return false
        return itemIsEnabled(item, featureSwitches, featureFlags, featureAccess)
      })
      if (items.length === 0) return []
      return [{ category, items }]
    })
  }, [
    mode,
    featureSwitches,
    featureFlags,
    featureAccess,
    hasBenefitEnrollment,
    hasPrimaryCareEnrollment,
  ])

  // Pass scrolling information down to child views that want it, e.g. for pagination.
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollEventTarget] = useState(new EventTarget())

  // Flat list of all currently-visible items for routing.
  const flatItems = useMemo(
    () => visibleCategories.flatMap(({ items }) => items),
    [visibleCategories],
  )

  // Every gate above (feature access, switches, flags, program enrollment) answers
  // asynchronously, so on the first render no gated item is visible yet and no
  // Route matches a deep link into one. Until they answer, the fallback below must
  // wait rather than redirect, or a link straight to a tab (ops reach Onboarding
  // that way from the signup-block queue) bounces to another tab before its own
  // route can mount. `isLoading` is false for a query that never runs, so a
  // disabled gate cannot leave the fallback waiting forever.
  const gatesPending = [
    featureAccessQuery,
    featureSwitchesQuery,
    featureFlagsQuery,
    programEnrollmentsQuery,
  ].some(query => query.isLoading)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PatientDetailNavStrip categories={visibleCategories} />
      <Box
        ref={scrollRef}
        onScroll={e => scrollEventTarget.dispatchEvent(new CustomEvent('scroll', { ...e }))}
        sx={{ flex: 1, overflow: 'auto' }}
      >
        <Suspense fallback={<Loader />}>
          <Routes>
            {flatItems.map(item => {
              // A person-mode variant renders with the raw Person record; every other view gets
              // the synthetic PatientUser.
              const PatientView = item.component
              const PersonView = mode === 'person' ? item.personComponent : undefined
              return (
                <Route
                  key={item.to}
                  path={`${item.to.replace(/^\//, '')}/*`}
                  element={
                    <ErrorBoundary FallbackComponent={ErrorScreen}>
                      {PersonView && person ? (
                        <PersonView
                          patient={person}
                          scrollRef={scrollRef}
                          scrollEventTarget={scrollEventTarget}
                        />
                      ) : (
                        <PatientView
                          patient={patient}
                          scrollRef={scrollRef}
                          scrollEventTarget={scrollEventTarget}
                        />
                      )}
                    </ErrorBoundary>
                  }
                />
              )
            })}
            <Route
              path="*"
              element={
                gatesPending ? (
                  <Loader />
                ) : (
                  <Navigate to={`${basePath}${flatItems[0]?.to ?? DEFAULT_ITEM_PATH}`} replace />
                )
              }
            />
          </Routes>
        </Suspense>
      </Box>
    </Box>
  )
}

export default PatientDetailNav
